import { useEffect, useMemo, useRef } from 'react';
import isEqual from 'react-fast-compare';
import { useInternalNotesFilter } from 'src/components/Filters/InternalNotesFilter';
import { useMergedTagFilters } from 'src/components/MainFilterBar/Tags/useMergedTagFilters';
import { SellerAccountEmployeeSelector } from 'src/components/Selectors/SellerAccountEmployeeSelector';
import { useContent } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosEnumMultiSelect } from 'src/core/POS/PosMultiSelect/PosEnumMultiSelect';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui/Stack';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  EVENT_MAPPING_STATE_FILTER_TO_CID,
  EVENT_PRICED_STATUS_TO_CID,
  PRE_DELIVERY_STATE_TO_LABEL,
  TICKET_TYPE_TO_CID,
  YES_NO_ENUM_FILTER_TO_CID,
  YES_NO_INVERTED_ENUM_FILTER_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { InhandDateRangePresetNames } from 'src/utils/dateTimeUtils';
import {
  EventMappingStateFilter,
  FromEventMappingStateFilter,
  FromYesNoEnum,
  ToEventMappingStateFilter,
  ToYesNoEnum,
} from 'src/utils/eventQueryUtils';
import { ReportTypes } from 'src/utils/reportsUtils';
import {
  ActionOutboxEntityType,
  Feature,
  ListingQuery,
  Permission,
  PreDeliveryState,
} from 'src/WebApiController';

import {
  FilterToolbarGroup,
  FilterToolbarItem,
  FilterToolbarItemId,
} from '../Filters';
import { useDefaultYesNoFilterValues } from '../Filters/hooks/useDefaultYesNoFilterValues';
import { RowFilter } from '../Filters/RowFilter';
import { SectionFilter } from '../Filters/SectionFilter';
import { PriceUpdatedDateStackedFilter } from '../Filters/StackedFilter/PriceUpdatedDateStackedFilter';
import { SaleDateStackedFilter } from '../Filters/StackedFilter/SaleDateStackedFilter';
import {
  CurrencyFilterMultiSelector,
  CurrencyFilterSelector,
} from '../Selectors/CurrencyFilterSelector';
import { DeliveryTypeSelector } from '../Selectors/DeliveryTypeSelector';
import { MAX_NUMBER_OF_TICKETS_HELD } from './MainFilterBar.constants';
import { useAutoPricingResultFilter } from './useAutoPricingResultFilter';
import {
  CommonEventFiltersProps,
  useCommonEventFilters,
} from './useCommonEventFilters';
import { useEventLastReviewedFilters } from './useEventLastReviewedFilter';
import { usePurchaseInfoFilters } from './usePurchaseInfoFilters';

export const listingsMandatoryFiltersToShow: FilterToolbarItemId[] = [
  'eventDates',
];

export function useListingFilters({
  isEventPage,
  isReportsPage,
  isHorizontalLayout,
  showCatalogFilters,
  showVenueFilter,
  showPerformerFilter,
  firstRenderOpen,
}: {
  isEventPage?: boolean;
  isReportsPage?: boolean;
  isHorizontalLayout?: boolean;
  showCatalogFilters?: CommonEventFiltersProps['showCatalogFilters'];
  showVenueFilter?: CommonEventFiltersProps['showVenueFilter'];
  showPerformerFilter?: CommonEventFiltersProps['showPerformerFilter'];
  firstRenderOpen?: boolean;
}) {
  const { initialQuery, tempQuery, setTempQuery } =
    useFilterQueryContext<ListingQuery>();

  const minText = useContent(ContentId.Min);
  const maxText = useContent(ContentId.Max);

  // Track which field was last edited for debounced validation
  const lastEditedFieldRef = useRef<'start' | 'end' | null>(null);

  // Debounced cross-field validation for numberOfTicketsHeld
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const start = tempQuery.numberOfTicketsHeld?.start;
      const end = tempQuery.numberOfTicketsHeld?.end;

      // Only validate if both fields have values and they conflict
      if (
        start !== null &&
        start !== undefined &&
        end !== null &&
        end !== undefined &&
        start > end
      ) {
        const lastEdited = lastEditedFieldRef.current;

        if (lastEdited === 'start') {
          // User last edited min, clear max
          setTempQuery({
            ...tempQuery,
            numberOfTicketsHeld: {
              start,
              end: null,
            },
          });
        } else if (lastEdited === 'end') {
          // User last edited max, clear min
          setTempQuery({
            ...tempQuery,
            numberOfTicketsHeld: {
              start: null,
              end,
            },
          });
        }
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [
    tempQuery.numberOfTicketsHeld?.start,
    tempQuery.numberOfTicketsHeld?.end,
    tempQuery,
    setTempQuery,
  ]);

  const purchaseInfoFilters = usePurchaseInfoFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    reportType: isReportsPage ? ReportTypes.Inventory : undefined,
    useRelativePresets: isReportsPage,
  });

  const tagsFilter = useMergedTagFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    entityType: ActionOutboxEntityType.Listing,
  });

  const eventLastReviewedFilters = useEventLastReviewedFilters({
    initialQuery,
    tempQuery,
    setTempQuery,
  });

  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const hasFilterByCurrencyCodeMultiFeature = useUserHasFeature(
    Feature.FilterByCurrencyCodeMulti
  );
  const hasFilterByCurrencyCodeFeature = !hasFilterByCurrencyCodeMultiFeature;

  const hasAutoFulfillSetting = useUserHasFeature(Feature.AutoFulfillSetting);

  const hasSeatSaverFeature = useUserHasFeature(Feature.SeatSaver);

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );

  const { shouldEnableEmptySelection } = useDefaultYesNoFilterValues({
    isReportsPage,
  });

  const { onUpdateResultTypes, pricingResultTypeToContentId } =
    useAutoPricingResultFilter(tempQuery, setTempQuery);

  const autoPricingFilter = useMemo<FilterToolbarItem[]>(
    () => [
      {
        filterId: 'isAutoPricingEnabled',
        labelContentId: ContentId.AutoPricingEnabled,
        filterQueryKeys: ['isAutoPricingEnabled'],
        filterItem: (
          <PosEnumSelect
            style={{ width: '100%' }}
            value={ToYesNoEnum(tempQuery.isAutoPricingEnabled)}
            enableEmptySelection={shouldEnableEmptySelection(
              'isAutoPricingEnabled'
            )}
            placeholderText={ContentId.All}
            onChange={(yesNoEnumValue) => {
              if (
                yesNoEnumValue !== ToYesNoEnum(tempQuery.isAutoPricingEnabled)
              ) {
                setTempQuery({
                  ...tempQuery,
                  isAutoPricingEnabled: FromYesNoEnum(yesNoEnumValue),
                });
              }
            }}
            valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
          />
        ),
      },
      ...[
        {
          filterId: 'autoPricingResultTypes' as FilterToolbarItemId,
          labelContentId: ContentId.AutoPricingResultType,
          filterQueryKeys: ['autoPricingResultTypes'] as FilterToolbarItemId[],
          filterItem: (
            <PosEnumMultiSelect
              triggerProps={{ style: { width: '100%' } }}
              values={tempQuery.autoPricingResultTypes || []}
              placeholderText={ContentId.AutoPricingAllResultTypeOption}
              enableEmptySelection
              onChange={({ newValues: autoPricingResultTypes }) => {
                onUpdateResultTypes(autoPricingResultTypes, null);
              }}
              valueOptionsContent={pricingResultTypeToContentId}
            />
          ),
        },
      ],
    ],
    [
      onUpdateResultTypes,
      pricingResultTypeToContentId,
      setTempQuery,
      shouldEnableEmptySelection,
      tempQuery,
    ]
  );

  const adminHoldFilter = useMemo<FilterToolbarItem[]>(
    () =>
      [
        {
          filterId: 'isAdminHold',
          labelContentId: ContentId.Hold,
          filterQueryKeys: ['isAdminHold'],
          filterItem: (
            <PosEnumSelect
              style={{ width: '100%' }}
              value={ToYesNoEnum(tempQuery.isAdminHold)}
              placeholderText={ContentId.All}
              onChange={(yesNoEnumValue) => {
                if (yesNoEnumValue !== ToYesNoEnum(tempQuery.isAdminHold)) {
                  setTempQuery({
                    ...tempQuery,
                    isAdminHold: FromYesNoEnum(yesNoEnumValue),
                  });
                }
              }}
              valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
            />
          ),
        },
      ] as FilterToolbarItem[],
    [setTempQuery, tempQuery]
  );

  const seatingFilters = useMemo<FilterToolbarGroup>(
    () => ({
      titleContentId: ContentId.Seating,
      type: 'row',
      items: [
        {
          filterId: 'sectionContains',
          labelContentId: ContentId.Section,
          filterQueryKeys: [
            'sectionEquals',
            'sectionContains',
            'sectionNotContains',
          ],
          filterItem: (
            <SectionFilter
              query={tempQuery}
              setQuery={setTempQuery}
              isHorizontalLayout={isHorizontalLayout}
            />
          ),
        },
        {
          filterId: 'rowContains',
          labelContentId: ContentId.Row,
          filterQueryKeys: ['rowEquals', 'rowContains', 'rowNotContains'],
          filterItem: (
            <RowFilter
              query={tempQuery}
              setQuery={setTempQuery}
              isHorizontalLayout={isHorizontalLayout}
            />
          ),
        },
      ],
    }),
    [isHorizontalLayout, setTempQuery, tempQuery]
  );

  const additionalEventFilters = useMemo(
    () =>
      [
        ...eventLastReviewedFilters,
        ...(hasAdPlatformViewPermission
          ? [
              {
                filterId: 'isBoosted' as FilterToolbarItemId,
                labelContentId: ContentId.Boosted,
                filterQueryKeys: ['isBoosted'] as FilterToolbarItemId[],
                filterItem: (
                  <PosEnumSelect
                    style={{ width: '100%' }}
                    enableEmptySelection={shouldEnableEmptySelection(
                      'isBoosted'
                    )}
                    value={ToYesNoEnum(tempQuery.isBoosted)}
                    placeholderText={ContentId.All}
                    onChange={(yesNoEnumValue) => {
                      if (yesNoEnumValue !== ToYesNoEnum(tempQuery.isBoosted)) {
                        setTempQuery({
                          ...tempQuery,
                          isBoosted: FromYesNoEnum(yesNoEnumValue),
                        });
                      }
                    }}
                    valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                  />
                ),
              },
            ]
          : []),
        {
          filterId: 'eventHasUnsoldTickets' as FilterToolbarItemId,
          labelContentId: ContentId.HasUnsoldListings,
          filterQueryKeys: ['eventHasUnsoldTickets'] as FilterToolbarItemId[],
          filterItem: (
            <PosEnumSelect
              style={{ width: '100%' }}
              enableEmptySelection={shouldEnableEmptySelection(
                'eventHasUnsoldTickets'
              )}
              value={ToYesNoEnum(tempQuery.eventHasUnsoldTickets)}
              placeholderText={ContentId.All}
              onChange={(yesNoEnumValue) => {
                if (
                  yesNoEnumValue !==
                  ToYesNoEnum(tempQuery.eventHasUnsoldTickets)
                ) {
                  setTempQuery({
                    ...tempQuery,
                    eventHasUnsoldTickets: FromYesNoEnum(yesNoEnumValue),
                  });
                }
              }}
              valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
            />
          ),
        },
        {
          filterId: 'noSaleDates',
          labelContentId: ContentId.SaleDateFilterTitle,
          filterQueryKeys: ['lastSaleDates', 'noSaleDates'],
          filterItem: <SaleDateStackedFilter />,
        },
        {
          filterId: 'numberOfTicketsHeld',
          labelContentId: ContentId.NumberOfTicketsHeld,
          filterQueryKeys: ['numberOfTicketsHeld'],
          filterItem: (
            <Stack
              direction="row"
              gap="m"
              alignItems="center"
              style={isHorizontalLayout ? { minWidth: '160px' } : undefined}
            >
              <PosTextField
                type="number"
                inputMode="numeric"
                min={0}
                max={MAX_NUMBER_OF_TICKETS_HELD}
                value={tempQuery.numberOfTicketsHeld?.start ?? ''}
                onChange={(e) => {
                  const value = e.target.value
                    ? parseInt(e.target.value)
                    : null;
                  const newStart = value === 0 ? null : value;

                  // Track that min field was last edited
                  lastEditedFieldRef.current = 'start';

                  // Only update the field being typed, no cross-validation here
                  if (newStart !== tempQuery.numberOfTicketsHeld?.start) {
                    setTempQuery({
                      ...tempQuery,
                      numberOfTicketsHeld: {
                        start: newStart,
                        end: tempQuery.numberOfTicketsHeld?.end ?? null,
                      },
                    });
                  }
                }}
                placeholder={minText}
                style={{ width: '100%' }}
              />
              <span>-</span>
              <PosTextField
                type="number"
                inputMode="numeric"
                min={0}
                max={MAX_NUMBER_OF_TICKETS_HELD}
                value={tempQuery.numberOfTicketsHeld?.end ?? ''}
                onChange={(e) => {
                  const value = e.target.value
                    ? parseInt(e.target.value)
                    : null;
                  const newEnd =
                    value === MAX_NUMBER_OF_TICKETS_HELD ? null : value;

                  // Track that max field was last edited
                  lastEditedFieldRef.current = 'end';

                  // Only update the field being typed, no cross-validation here
                  if (newEnd !== tempQuery.numberOfTicketsHeld?.end) {
                    setTempQuery({
                      ...tempQuery,
                      numberOfTicketsHeld: {
                        start: tempQuery.numberOfTicketsHeld?.start ?? null,
                        end: newEnd,
                      },
                    });
                  }
                }}
                placeholder={maxText}
                style={{ width: '100%' }}
              />
            </Stack>
          ),
        },
        {
          filterId: 'isMapped' as FilterToolbarItemId,
          labelContentId: ContentId.RequestState,
          filterQueryKeys: [
            'isMapped',
            'isMappingRejected',
          ] as FilterToolbarItemId[],
          filterItem: (
            <PosEnumSelect
              style={{ width: '100%' }}
              enableEmptySelection={shouldEnableEmptySelection('isMapped')}
              value={ToEventMappingStateFilter(
                tempQuery.isMapped,
                tempQuery.isMappingRejected
              )}
              placeholderText={ContentId.All}
              onChange={(filterEnumValue) => {
                if (
                  filterEnumValue !==
                  ToEventMappingStateFilter(
                    tempQuery.isMapped,
                    tempQuery.isMappingRejected
                  )
                ) {
                  const [isMapped, isMappingRejected] =
                    FromEventMappingStateFilter(
                      filterEnumValue ?? EventMappingStateFilter.All
                    );
                  setTempQuery({
                    ...tempQuery,
                    isMapped,
                    isMappingRejected,
                  });
                }
              }}
              valueOptionsContent={EVENT_MAPPING_STATE_FILTER_TO_CID}
            />
          ),
        },
        {
          filterId: 'eventUnpricedStatus' as FilterToolbarItemId,
          labelContentId: ContentId.HasUnpricedListings,
          filterQueryKeys: ['eventUnpricedStatus'] as FilterToolbarItemId[],
          filterItem: (
            <PosEnumSelect
              style={{ width: '100%' }}
              value={tempQuery.eventUnpricedStatus}
              enableEmptySelection={shouldEnableEmptySelection(
                'eventUnpricedStatus'
              )}
              onChange={(eventUnpricedStatus) => {
                if (eventUnpricedStatus !== tempQuery.eventUnpricedStatus) {
                  setTempQuery({
                    ...tempQuery,
                    eventUnpricedStatus,
                  });
                }
              }}
              valueOptionsContent={EVENT_PRICED_STATUS_TO_CID}
            />
          ),
        },
      ] as FilterToolbarItem[],
    [
      eventLastReviewedFilters,
      hasAdPlatformViewPermission,
      isHorizontalLayout,
      maxText,
      minText,
      setTempQuery,
      shouldEnableEmptySelection,
      tempQuery,
    ]
  );

  const eventFilters = useCommonEventFilters({
    showCatalogFilters,
    showVenueFilter,
    showPerformerFilter,
    firstRenderOpen,
    additionalFilters: additionalEventFilters,
  });
  const eventFilter = isEventPage ? undefined : eventFilters;

  const notesFilters = useInternalNotesFilter<ListingQuery>({
    entityType: ActionOutboxEntityType.Listing,
    isHorizontalLayout,
  });

  const metricsIconNote = useContent(ContentId.ThisFilterWillChangeMetrics);

  const filters = useMemo<FilterToolbarGroup[]>(
    () =>
      [
        eventFilter,
        {
          titleContentId: ContentId.Pricing,
          type: 'row',
          items: [
            {
              filterId: 'pricerSellerUserId' as FilterToolbarItemId,
              filterQueryKeys: ['pricerSellerUserId'] as FilterToolbarItemId[],
              labelContentId: ContentId.PricedBy,
              filterItem: (
                <SellerAccountEmployeeSelector
                  value={tempQuery.pricerSellerUserId ?? undefined}
                  enableEmptySelection
                  style={{ width: '100%' }}
                  onChange={(newId) => {
                    if (newId !== tempQuery.pricerSellerUserId) {
                      setTempQuery({
                        ...tempQuery,
                        pricerSellerUserId: newId,
                      });
                    }
                  }}
                />
              ),
            },
            ...(hasAutoPricingFeature ? autoPricingFilter : []),

            {
              filterId: 'isPricedAboveZero' as FilterToolbarItemId,
              labelContentId: ContentId.Priced,
              filterQueryKeys: ['isPricedAboveZero'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isPricedAboveZero)}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'isPricedAboveZero'
                  )}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !==
                      ToYesNoEnum(tempQuery.isPricedAboveZero)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        isPricedAboveZero: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            {
              filterId: 'lastPriceUpdateDates' as FilterToolbarItemId,
              filterQueryKeys: [
                'lastPriceUpdateDates',
                'lastPriceNoUpdateDates',
              ] as FilterToolbarItemId[],
              labelContentId: ContentId.PriceDateFilterTitle,
              filterItem: <PriceUpdatedDateStackedFilter />,
            },

            {
              labelContentId: ContentId.IsPricedByMarketplace,
              filterId: 'isPricedByMarketplace' as FilterToolbarItemId,
              filterQueryKeys: [
                'isPricedByMarketplace',
              ] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isPricedByMarketplace)}
                  placeholderText={ContentId.All}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'isPricedByMarketplace'
                  )}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !==
                      ToYesNoEnum(tempQuery.isPricedByMarketplace)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        isPricedByMarketplace: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },

            ...(hasFilterByCurrencyCodeFeature
              ? [
                  {
                    filterId: 'currencyCode' as FilterToolbarItemId,
                    labelContentId: ContentId.Currency,
                    filterQueryKeys: ['currencyCode'] as FilterToolbarItemId[],
                    filterItem: (
                      <CurrencyFilterSelector
                        value={tempQuery.currencyCode ?? ''}
                        style={{ width: '100%' }}
                        placeholderText={ContentId.All}
                        onChange={(newCurrencyCode) => {
                          if (tempQuery.currencyCode !== newCurrencyCode) {
                            setTempQuery({
                              ...tempQuery,
                              currencyCode: newCurrencyCode?.length
                                ? newCurrencyCode
                                : null,
                            });
                          }
                        }}
                        enableEmptySelection
                      />
                    ),
                  },
                ]
              : []),
            ...(hasFilterByCurrencyCodeMultiFeature
              ? [
                  {
                    filterId: 'currencyCodes' as FilterToolbarItemId,
                    labelContentId: ContentId.Currency,
                    filterQueryKeys: ['currencyCodes'] as FilterToolbarItemId[],
                    filterItem: (
                      <CurrencyFilterMultiSelector
                        values={tempQuery.currencyCodes ?? []}
                        triggerProps={{ style: { width: '100%' } }}
                        placeholderText={ContentId.All}
                        onChange={({ newValues: newCurrencyCodes }) => {
                          if (
                            !isEqual(tempQuery.currencyCodes, newCurrencyCodes)
                          ) {
                            setTempQuery({
                              ...tempQuery,
                              currencyCodes: newCurrencyCodes?.length
                                ? newCurrencyCodes
                                : null,
                            });
                          }
                        }}
                        enableEmptySelection
                      />
                    ),
                  },
                ]
              : []),
          ],
        },
        {
          titleContentId: ContentId.Broadcast,
          type: 'row',
          items: [
            {
              filterId: 'isListed' as FilterToolbarItemId,
              labelContentId: ContentId.Broadcasted,
              filterQueryKeys: ['isListed'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isListed)}
                  enableEmptySelection={shouldEnableEmptySelection('isListed')}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (yesNoEnumValue !== ToYesNoEnum(tempQuery.isListed)) {
                      setTempQuery({
                        ...tempQuery,
                        isListed: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            {
              filterId: 'hasBroadcastError' as FilterToolbarItemId,
              labelContentId: ContentId.HasBroadcastError,
              filterQueryKeys: ['hasBroadcastError'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.hasBroadcastError)}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'hasBroadcastError'
                  )}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !==
                      ToYesNoEnum(tempQuery.hasBroadcastError)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        hasBroadcastError: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            ...adminHoldFilter,
            {
              filterId: 'isDuplicate' as FilterToolbarItemId,
              labelContentId: ContentId.IsDuplicate,
              filterQueryKeys: ['isDuplicate'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isDuplicate)}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'isDuplicate'
                  )}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (yesNoEnumValue !== ToYesNoEnum(tempQuery.isDuplicate)) {
                      setTempQuery({
                        ...tempQuery,
                        isDuplicate: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },

            {
              filterId: 'isPendingProof' as FilterToolbarItemId,
              labelContentId: ContentId.ProofOfPurchasedRequired,
              filterQueryKeys: ['isPendingProof'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isPendingProof)}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'isPendingProof'
                  )}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !== ToYesNoEnum(tempQuery.isPendingProof)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        isPendingProof: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
          ],
        },
        {
          titleContentId: ContentId.Fulfillment,
          type: 'row',
          items: [
            {
              filterId: 'preDeliveryStates' as FilterToolbarItemId,
              labelContentId: ContentId.PreDelivered,
              type: 'row',
              filterQueryKeys: ['preDeliveryStates'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={tempQuery.preDeliveryStates?.[0] ?? null}
                  onChange={(preDeliveryState) => {
                    setTempQuery({
                      ...tempQuery,
                      preDeliveryStates: preDeliveryState
                        ? [preDeliveryState]
                        : null,
                    });
                  }}
                  valueOptionsContent={PRE_DELIVERY_STATE_TO_LABEL}
                />
              ),
            },
            {
              filterId: 'deliveryType' as FilterToolbarItemId,
              labelContentId: ContentId.Delivery,
              filterQueryKeys: ['deliveryType'] as FilterToolbarItemId[],
              filterItem: (
                <DeliveryTypeSelector
                  style={{ width: '100%' }}
                  value={tempQuery.deliveryType}
                  enableEmptySelection
                  onChange={(deliveryType) => {
                    if (deliveryType !== tempQuery.deliveryType) {
                      setTempQuery({
                        ...tempQuery,
                        deliveryType: deliveryType,
                      });
                    }
                  }}
                  allowCustomDeliveryType
                />
              ),
            },
            {
              filterId: 'ticketType' as FilterToolbarItemId,
              labelContentId: ContentId.TicketType,
              filterQueryKeys: ['ticketType'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={tempQuery.ticketType}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'ticketType'
                  )}
                  placeholderText={ContentId.AllTicketTypes}
                  onChange={(ticketType) => {
                    if (ticketType !== tempQuery.ticketType) {
                      setTempQuery({
                        ...tempQuery,
                        ticketType: ticketType || null,
                      });
                    }
                  }}
                  valueOptionsContent={TICKET_TYPE_TO_CID}
                />
              ),
            },
            ...(hasAutoFulfillSetting
              ? [
                  {
                    filterId: 'isNoFulfill' as FilterToolbarItemId,
                    labelContentId: ContentId.AutoFulfill,
                    type: 'row',
                    filterQueryKeys: ['isNoFulfill'] as FilterToolbarItemId[],
                    filterItem: (
                      <PosEnumSelect
                        style={{ width: '100%' }}
                        value={ToYesNoEnum(
                          // the ui is the inverse of isNoFulfil
                          tempQuery.isNoFulfill == null
                            ? null
                            : !tempQuery.isNoFulfill
                        )}
                        placeholderText={ContentId.All}
                        enableEmptySelection={shouldEnableEmptySelection(
                          'isNoFulfill'
                        )}
                        onChange={(yesNoEnumValue) => {
                          if (
                            yesNoEnumValue !==
                            ToYesNoEnum(
                              // the ui is the inverse of isNoFulfil
                              tempQuery.isNoFulfill == null
                                ? null
                                : !tempQuery.isNoFulfill
                            )
                          ) {
                            const v = FromYesNoEnum(yesNoEnumValue);
                            setTempQuery({
                              ...tempQuery,
                              isNoFulfill: v == null ? null : !v, // the ui is the inverse of isNoFulfil
                            });
                          }
                        }}
                        valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                      />
                    ),
                  },
                ]
              : []),
            {
              filterId: 'hasUnsoldTickets' as FilterToolbarItemId,
              labelContentId: ContentId.HasUnsoldTickets,
              filterQueryKeys: ['hasUnsoldTickets'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.hasUnsoldTickets)}
                  placeholderText={ContentId.All}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'hasUnsoldTickets'
                  )}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !== ToYesNoEnum(tempQuery.hasUnsoldTickets)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        hasUnsoldTickets: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            {
              filterId: 'inHandDates' as const,
              labelContentId: ContentId.InHand,
              filterQueryKeys: ['inHandDates'] as FilterToolbarItemId[],
              filterItem: (
                <DateRangeSelector
                  useRelativePresets={false}
                  presetNames={InhandDateRangePresetNames}
                  exactPresetNames={InhandDateRangePresetNames}
                  trailingPresetNames={InhandDateRangePresetNames}
                  value={tempQuery.inHandDates}
                  defaultValue={initialQuery.inHandDates}
                  onBlur={({ value }) =>
                    setTempQuery({
                      ...tempQuery,
                      inHandDates: value,
                    })
                  }
                />
              ),
            },
            ...(hasSeatSaverFeature
              ? [
                  {
                    filterId: 'isSeatSaver' as const,
                    labelContentId: ContentId.Placeholder,
                    filterQueryKeys: ['isSeatSaver'] as FilterToolbarItemId[],
                    filterItem: (
                      <PosEnumSelect
                        style={{ width: '100%' }}
                        value={ToYesNoEnum(tempQuery.isSeatSaver)}
                        enableEmptySelection={shouldEnableEmptySelection(
                          'isSeatSaver'
                        )}
                        placeholderText={ContentId.All}
                        onChange={(yesNoEnumValue) => {
                          if (
                            yesNoEnumValue !==
                            ToYesNoEnum(tempQuery.isSeatSaver)
                          ) {
                            setTempQuery({
                              ...tempQuery,
                              isSeatSaver: FromYesNoEnum(yesNoEnumValue),
                            });
                          }
                        }}
                        valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                      />
                    ),
                  },
                ]
              : []),
          ],
        },
        {
          titleContentId: ContentId.Mappings,
          type: 'row',
          items: [
            {
              filterId: 'isMapped' as FilterToolbarItemId,
              labelContentId: ContentId.EventMapped,
              filterQueryKeys: ['isMapped'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isMapped)}
                  enableEmptySelection={shouldEnableEmptySelection('isMapped')}
                  placeholderText={ContentId.All}
                  onChange={(yesNoEnumValue) => {
                    if (yesNoEnumValue !== ToYesNoEnum(tempQuery.isMapped)) {
                      setTempQuery({
                        ...tempQuery,
                        isMapped: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                />
              ),
            },
            {
              filterId: 'isSeatingUnmapped' as FilterToolbarItemId,
              labelContentId: ContentId.SeatingMapped,
              filterQueryKeys: ['isSeatingUnmapped'] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(tempQuery.isSeatingUnmapped)}
                  placeholderText={ContentId.All}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'isSeatingUnmapped'
                  )}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !==
                      ToYesNoEnum(tempQuery.isSeatingUnmapped)
                    ) {
                      setTempQuery({
                        ...tempQuery,
                        isSeatingUnmapped: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_NO_INVERTED_ENUM_FILTER_TO_CID}
                />
              ),
            },
          ],
        },
        seatingFilters,
        purchaseInfoFilters,
        notesFilters,
        ...tagsFilter,
      ]
        .filter((f) => f)
        .map((f) => f!),
    [
      eventFilter,
      tempQuery,
      hasAutoPricingFeature,
      autoPricingFilter,
      shouldEnableEmptySelection,
      hasFilterByCurrencyCodeFeature,
      hasFilterByCurrencyCodeMultiFeature,
      adminHoldFilter,
      hasAutoFulfillSetting,
      initialQuery.inHandDates,
      hasSeatSaverFeature,
      seatingFilters,
      purchaseInfoFilters,
      notesFilters,
      tagsFilter,
      setTempQuery,
    ]
  );

  return { filters };
}
